import React, { useEffect, useState, useRef, useMemo } from 'react';
import { usePriorities } from '../../../hooks/usePriorities';
import { useCategories } from '../../../hooks/useCategories';
import { useStatuses } from '../../../hooks/useStatuses';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  consumeNotificationTicketSearch,
  consumeNotificationCustomerTicketOpen,
  CUSTOMER_PORTAL_TICKET_NOTIFICATION_EVENT,
} from '../../../utils/notificationNavigation';
import { ticketService, extractSavedReplyFromResponse } from '../../../api/ticketService';
import { fileService } from '../../../api/fileService';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '../../../components/ui';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../../../utils/errorHandler';

import { 
  Ticket, Plus, Search, Filter, Clock, CheckCircle, AlertCircle, XCircle, 
  MessageCircle, History, RefreshCw, ArrowUp,   Download, User, Eye,
  FileText, Paperclip, ChevronLeft, ChevronRight, MessageSquare, Send, X, RotateCcw, Star,
  ChevronDown, Loader2
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadStyledXlsx } from '../../../utils/exportStyledXlsx';
import { formatTicketFileUploadedAt } from '../../../utils/formatTicketFileUploadedAt';
import { useDisplayTimezone } from '../../../contexts/AppConfigContext';

/** URL to open attachment in browser (preview / new tab) — same rules as download flow */
function resolveAttachmentOpenUrl(attachment) {
  if (!attachment) return null;
  const documentPath = attachment.documentPath || attachment.document_path;
  if (documentPath) {
    const DOMAIN_URL =
      window.location.hostname === 'localhost'
        ? 'https://helpdesk-pro.dhanushinfotech.com'
        : window.location.origin;
    return `${DOMAIN_URL}${documentPath.startsWith('/') ? '' : '/'}${documentPath}`;
  }
  if (attachment.url && attachment.url !== '#') {
    const u = attachment.url;
    if (u.startsWith('http')) return u;
    const origin =
      window.location.hostname === 'localhost'
        ? 'https://helpdesk-pro.dhanushinfotech.com'
        : window.location.origin;
    return `${origin}${u.startsWith('/') ? '' : '/'}${u}`;
  }
  if (attachment.attachmentId) {
    return `${window.location.origin}/gateway/api/ticket/attachment/${attachment.attachmentId}/download`;
  }
  return null;
}

function transformPortalTicketRow(ticket, availablePriorities = []) {
  let priority = 'medium';
  let priorityName = 'Medium';
  let priorityId = ticket.priorityId;

  if (priorityId && availablePriorities.length > 0) {
    const priorityObj = availablePriorities.find(
      (p) => p.priorityId === priorityId || p.priorityId?.toString() === priorityId?.toString()
    );
    if (priorityObj) {
      priority = (priorityObj.priorityCode || priorityObj.priorityName || 'medium').toLowerCase();
      priorityName = priorityObj.priorityName || priorityObj.priorityCode || 'Medium';
    }
  } else if (ticket.priority) {
    priority = ticket.priority.toLowerCase();
    priorityName = ticket.priority;
  }

  return {
    id: ticket.ticketId || ticket.ticketNumber,
    ticketId: ticket.ticketId,
    ticketNumber: ticket.ticketNumber,
    subject: ticket.subject,
    description: ticket.description,
    customFieldValues: ticket.customFieldValues || {},
    status: ticket.status?.toLowerCase() || 'open',
    priority,
    priorityName,
    priorityId,
    category: ticket.category || 'Uncategorized',
    createdAt: ticket.createdAt,
    updatedAt: ticket.updatedAt,
    lastActivity: ticket.updatedAt || ticket.createdAt,
    assignedAgent: ticket.assigneeId
      ? { id: ticket.assigneeId, name: `${ticket.assigneeFullName}` }
      : null,
    slaStatus: ticket.slaBreach ? 'breach' : 'on-track',
    attachments: ticket.attachments || [],
    communications: ticket.communications || [],
    countryId: ticket.countryId,
    regionId: ticket.regionId,
    countyId: ticket.countyId,
    subCountyId: ticket.subCountyId,
    wardId: ticket.wardId,
    countryName: ticket.countryName,
    regionName: ticket.regionName,
    countyName: ticket.countyName,
    subCountyName: ticket.subCountyName,
    wardName: ticket.wardName,
  };
}

function matchPortalTicketRow(tickets, ticketNumber, ticketId) {
  if (!Array.isArray(tickets) || tickets.length === 0) {
    return null;
  }
  const normalizedNumber = ticketNumber ? String(ticketNumber).trim().toUpperCase() : null;
  const normalizedId = ticketId != null ? String(ticketId).trim() : null;

  return (
    tickets.find((ticket) => {
      if (normalizedNumber) {
        const rowNumber = String(ticket.ticketNumber || '').trim().toUpperCase();
        if (rowNumber === normalizedNumber) {
          return true;
        }
      }
      if (normalizedId) {
        const rowId = ticket.ticketId != null ? String(ticket.ticketId) : String(ticket.id);
        if (rowId === normalizedId) {
          return true;
        }
      }
      return false;
    }) || null
  );
}

function extractPortalTicketFromApiResponse(response) {
  const responseData = response?.data;
  if (responseData?.success && responseData?.data) {
    return responseData.data;
  }
  if (responseData?.data) {
    return responseData.data;
  }
  return responseData || null;
}

const MyTickets = ({ onCreateTicket }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Backend-configured display timezone (IANA zone id, e.g. "Africa/Nairobi"). Sourced
  // from `app.display-timezone` via /api/ticket/no-auth/config/display-timezone so all
  // browsers render reply timestamps in the same business wall-clock.
  const displayTimezone = useDisplayTimezone();
  const [ticketsData, setTicketsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    searchTerm: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [newReply, setNewReply] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const replyFileInputRef = useRef(null);
  const [isAddingReply, setIsAddingReply] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const { data: priorities = [], isSuccess: prioritiesReady } = usePriorities();
  const { data: categoriesRaw = [], isSuccess: categoriesReady } = useCategories();
  const { data: statusesRaw = [], isSuccess: statusesReady } = useStatuses();
  const categories = useMemo(
    () => categoriesRaw.filter((cat) => cat.isActive !== false && cat.categoryId !== null),
    [categoriesRaw]
  );
  const statuses = useMemo(
    () => statusesRaw.filter((status) => status.isActive !== false),
    [statusesRaw]
  );
  const referenceDataLoaded = prioritiesReady && categoriesReady && statusesReady;
  const [allTicketsStats, setAllTicketsStats] = useState(null); // Store stats from all tickets
  const [ticketFeedback, setTicketFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackStarsCount, setFeedbackStarsCount] = useState(0);
  const [feedbackHoveredStar, setFeedbackHoveredStar] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const prioritiesRef = useRef([]);
  const portalTicketsBootstrappedRef = useRef(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debouncedSearchTermRef = useRef('');
  const skipSearchDebounceRef = useRef(false);
  const prevSearchFiltersKeyRef = useRef(null);
  const lastTicketsLoadKeyRef = useRef(null);
  const pendingNotificationTicketOpenRef = useRef(null);
  const lastOpenedNotificationTicketKeyRef = useRef(null);
  const [notificationOpenTrigger, setNotificationOpenTrigger] = useState(0);

  const applyNotificationTicketSearch = (ticketNumber) => {
    if (!ticketNumber) {
      return;
    }
    const trimmed = String(ticketNumber).trim();
    skipSearchDebounceRef.current = true;
    setFilters((prev) => ({ ...prev, searchTerm: trimmed }));
    debouncedSearchTermRef.current = trimmed;
    setDebouncedSearchTerm(trimmed);
    lastTicketsLoadKeyRef.current = null;
    setCurrentPage(0);
  };

  const queueNotificationTicketOpen = (openPayload) => {
    if (!openPayload?.ticketNumber && !openPayload?.ticketId) {
      return;
    }
    const openKey = `${openPayload.ticketNumber || ''}|${openPayload.ticketId || ''}`;
    if (lastOpenedNotificationTicketKeyRef.current !== openKey) {
      lastOpenedNotificationTicketKeyRef.current = null;
    }
    pendingNotificationTicketOpenRef.current = openPayload;
    setNotificationOpenTrigger((count) => count + 1);
  };

  const openCustomerTicketFromNotification = async ({ ticketNumber, ticketId }) => {
    if (!ticketNumber && !ticketId) {
      return;
    }

    const openKey = `${ticketNumber || ''}|${ticketId || ''}`;
    if (lastOpenedNotificationTicketKeyRef.current === openKey) {
      return;
    }

    const availablePriorities = prioritiesRef.current;
    let matchedTicket = matchPortalTicketRow(
      ticketsData?.userTickets,
      ticketNumber,
      ticketId
    );

    if (!matchedTicket) {
      try {
        const params = {
          page: 0,
          size: 10,
          asRequester: true,
        };
        if (ticketNumber) {
          params.search = String(ticketNumber).trim();
        }
        const response = await ticketService.getMyTickets(params);
        const paginatedData = extractPortalTicketFromApiResponse(response);
        const tickets = Array.isArray(paginatedData?.content) ? paginatedData.content : [];
        const transformedTickets = tickets.map((ticket) =>
          transformPortalTicketRow(ticket, availablePriorities)
        );
        matchedTicket = matchPortalTicketRow(transformedTickets, ticketNumber, ticketId);
      } catch (fetchError) {
        console.error('Error resolving customer notification ticket from list:', fetchError);
      }
    }

    if (!matchedTicket && ticketId) {
      try {
        const response = await ticketService.getTicketById(ticketId);
        const rawTicket = extractPortalTicketFromApiResponse(response);
        if (rawTicket && typeof rawTicket === 'object') {
          matchedTicket = transformPortalTicketRow(rawTicket, availablePriorities);
        }
      } catch (fetchError) {
        console.error('Error resolving customer notification ticket by id:', fetchError);
      }
    }

    if (matchedTicket) {
      lastOpenedNotificationTicketKeyRef.current = openKey;
      setSelectedTicket(matchedTicket);
      return;
    }

    if (ticketNumber) {
      applyNotificationTicketSearch(ticketNumber);
    }
  };

  useEffect(() => {
    prioritiesRef.current = priorities;
  }, [priorities]);

  // Load stats once master reference data is available from React Query cache
  useEffect(() => {
    if (referenceDataLoaded) {
      loadTicketStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceDataLoaded]);

  const consumeQueuedCustomerNotificationOpen = () => {
    const openPayload = consumeNotificationCustomerTicketOpen();
    if (openPayload) {
      queueNotificationTicketOpen(openPayload);
      return true;
    }
    const notificationSearch = consumeNotificationTicketSearch();
    if (notificationSearch) {
      applyNotificationTicketSearch(notificationSearch);
      return true;
    }
    return false;
  };

  // Notification click: open customer ticket details modal (search patch kept as fallback)
  useEffect(() => {
    if (!referenceDataLoaded) {
      return undefined;
    }
    consumeQueuedCustomerNotificationOpen();
    portalTicketsBootstrappedRef.current = true;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceDataLoaded]);

  useEffect(() => {
    if (!portalTicketsBootstrappedRef.current) {
      return undefined;
    }
    consumeQueuedCustomerNotificationOpen();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    const handlePortalTicketNotification = () => {
      consumeQueuedCustomerNotificationOpen();
    };
    window.addEventListener(CUSTOMER_PORTAL_TICKET_NOTIFICATION_EVENT, handlePortalTicketNotification);
    return () => {
      window.removeEventListener(
        CUSTOMER_PORTAL_TICKET_NOTIFICATION_EVENT,
        handlePortalTicketNotification
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!referenceDataLoaded || !pendingNotificationTicketOpenRef.current) {
      return undefined;
    }

    const openPayload = { ...pendingNotificationTicketOpenRef.current };
    let cancelled = false;

    const run = async () => {
      await openCustomerTicketFromNotification(openPayload);
      if (!cancelled) {
        pendingNotificationTicketOpenRef.current = null;
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceDataLoaded, notificationOpenTrigger, ticketsData]);

  // Debounce search input — API uses debouncedSearchTerm, not raw keystrokes
  useEffect(() => {
    if (skipSearchDebounceRef.current) {
      skipSearchDebounceRef.current = false;
      return undefined;
    }

    const trimmed = filters.searchTerm.trim();

    // Skip API for single-character queries; wait until user types more or clears
    if (trimmed.length === 1) {
      return undefined;
    }

    const delay = trimmed.length === 0 ? 300 : 500;
    const timeoutId = setTimeout(() => {
      debouncedSearchTermRef.current = trimmed;
      setDebouncedSearchTerm(trimmed);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [filters.searchTerm]);

  // Load paginated tickets when page, pageSize, status/category, or debounced search changes
  useEffect(() => {
    if (!referenceDataLoaded) {
      return;
    }

    const filtersKey = JSON.stringify({
      status: filters.status,
      category: filters.category,
      search: debouncedSearchTerm,
    });
    const filtersChanged =
      prevSearchFiltersKeyRef.current !== null
      && prevSearchFiltersKeyRef.current !== filtersKey;
    prevSearchFiltersKeyRef.current = filtersKey;

    if (filtersChanged && currentPage !== 0) {
      setCurrentPage(0);
      return;
    }

    const loadKey = `${filtersKey}|${currentPage}|${pageSize}`;
    if (lastTicketsLoadKeyRef.current === loadKey) {
      return;
    }
    lastTicketsLoadKeyRef.current = loadKey;

    loadTicketsData(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, referenceDataLoaded, filters.status, filters.category, debouncedSearchTerm]);

  useEffect(() => {
    if (!selectedTicket) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    const prevHtmlOs = html.style.overscrollBehavior;
    const prevBodyOs = body.style.overscrollBehavior;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
      html.style.overscrollBehavior = prevHtmlOs;
      body.style.overscrollBehavior = prevBodyOs;
    };
  }, [selectedTicket]);

  const defaultTicketStats = () => ({
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    resolvedTickets: 0,
    closedTickets: 0,
    highPriorityTickets: 0,
    overdueTickets: 0,
    avgResolutionTime: '0h 0m'
  });

  const loadTicketStatistics = async () => {
    try {
      const response = await ticketService.getMyTicketsStatistics({ asRequester: true });
      const responseData = response?.data;
      const stats = responseData?.data;

      if (responseData?.success && stats) {
        setAllTicketsStats({
          totalTickets: Number(stats.total) || 0,
          openTickets: Number(stats.open) || 0,
          inProgressTickets: Number(stats['in-progress']) || 0,
          resolvedTickets: Number(stats.resolved) || 0,
          closedTickets: Number(stats.closed) || 0,
          highPriorityTickets: 0,
          overdueTickets: 0,
          avgResolutionTime: '0h 0m'
        });
      } else {
        setAllTicketsStats(defaultTicketStats());
      }
    } catch (statsError) {
      console.warn('Error fetching ticket statistics:', statsError);
      setAllTicketsStats(defaultTicketStats());
    }
  };

  const loadTicketsData = async (page = 0, size = 10) => {
    try {
      setLoading(true);

      const availablePriorities = prioritiesRef.current;
      
      // Build API parameters with filters
      const params = { 
        page, 
        size, 
        asRequester: true 
      };
      
      // Add status filter if not 'all'
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }
      
      // Add category filter if not 'all'
      if (filters.category && filters.category !== 'all') {
        // Find categoryId from category name
        const selectedCategory = categories.find(cat => 
          (cat.categoryName || '').toLowerCase() === filters.category.toLowerCase()
        );
        if (selectedCategory && selectedCategory.categoryId) {
          params.categoryId = selectedCategory.categoryId;
        }
      }
      
      // Add search filter if provided (debounced term — not raw keystrokes)
      const searchQuery = debouncedSearchTermRef.current;
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      // Fetch paginated tickets for display with filters
      const response = await ticketService.getMyTickets(params);
      
      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      // data is PaginatedResponse with content, pageNumber, pageSize, totalElements, etc.
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        const tickets = paginatedData.content || [];
        
        // Transform backend tickets to frontend format
        const transformedTickets = tickets.map((ticket) =>
          transformPortalTicketRow(ticket, availablePriorities)
        );

        // Use stats from ALL tickets (stored in allTicketsStats state), not just current page
        const stats = allTicketsStats || {
          totalTickets: paginatedData.totalElements || 0,
          openTickets: 0,
          inProgressTickets: 0,
          resolvedTickets: 0,
          closedTickets: 0,
          highPriorityTickets: 0,
          overdueTickets: 0,
          avgResolutionTime: '0h 0m'
        };

        // Create filters from tickets
        const statuses = [...new Set(transformedTickets.map(t => t.status))].map(status => ({
          value: status,
          label: status.charAt(0).toUpperCase() + status.slice(1),
          count: transformedTickets.filter(t => t.status === status).length
        }));

        const priorities = [...new Set(transformedTickets.map(t => t.priority))].map(priority => ({
          value: priority,
          label: priority.charAt(0).toUpperCase() + priority.slice(1),
          count: transformedTickets.filter(t => t.priority === priority).length
        }));

        // Use categories from API instead of extracting from tickets
        const categoryFilters = categories.map(category => ({
          value: (category.categoryName || '').toLowerCase(),
          label: category.categoryName || 'Uncategorized',
          categoryId: category.categoryId
        }));

        setTicketsData({
          userTickets: transformedTickets,
          ticketStats: stats,
          ticketFilters: {
            statuses,
            priorities,
            categories: categoryFilters
          },
          ticketActions: []
        });
        
        // Store pagination data
        setPaginationData({
          pageNumber: paginatedData.pageNumber || page,
          pageSize: paginatedData.pageSize || size,
          totalElements: paginatedData.totalElements || 0,
          totalPages: paginatedData.totalPages || 0,
          first: paginatedData.first !== undefined ? paginatedData.first : (page === 0),
          last: paginatedData.last !== undefined ? paginatedData.last : false
        });
      } else {
        setError(responseData?.message || 'Failed to load tickets data');
        toast.error(responseData?.message || 'Failed to load tickets');
      }
    } catch (err) {
      setError('Error loading tickets data');
      console.error('Error loading tickets:', err);
      toast.error(err?.response?.data?.message || 'Error loading tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = () => {
    try {
      if (onCreateTicket) {
        onCreateTicket();
      } else {
        // Default behavior: navigate to create ticket page
        navigate('/portal/tickets/create', { replace: false });
      }
    } catch (error) {
      console.error('❌ Error in handleCreateTicket:', error);
      toast.error('Failed to open create ticket page. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
      case 'open': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
      case 'pending': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'in-progress': 
      case 'in_progress': return 'text-white dark:text-white bg-teal-600 dark:bg-teal-700';
      case 'resolved': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'closed': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'on-hold':
      case 'on_hold':
      case 'on hold': return 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900';
      case 'cancelled':
      case 'canceled': return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
      case 'reopen': 
      case 're-open': return 'text-white dark:text-white bg-red-700 dark:bg-red-800';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'urgent': return 'bg-red-800 text-red-50';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get light background color for ticket card based on priority
  const getPriorityBackgroundColor = (priority) => {
    // Return default background - no priority-based colors
    return 'bg-white';
  };

  // Get darker border color for ticket card based on priority
  const getPriorityBorderColor = (priority) => {
    // Return default border - no priority-based colors
    return 'border-gray-300';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="h-4 w-4" />;
      case 'in-progress': return <Clock className="h-4 w-4" />;
      case 'resolved': return <CheckCircle className="h-4 w-4" />;
      case 'closed': return <XCircle className="h-4 w-4" />;
      default: return <Ticket className="h-4 w-4" />;
    }
  };

  const getSlaStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'breach': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  // const formatDateTime = (isoString) => {
  //   const date = new Date(isoString);
  //   const now = new Date();
  //   const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
  //   if (diffInHours < 1) {
  //     return 'Just now';
  //   } 
  //   else if (diffInHours >= 24) {
  //    const date = new Date(date);

  // return (
  //   date.toLocaleDateString() +
  //   ' ' +
  //   date.toLocaleTimeString([], {
  //     hour: '2-digit',
  //     minute: '2-digit',
  //     second: '2-digit'
  //   })
  // );
  //   }
  //   else if (diffInHours < 24) {
  //     return `${diffInHours}h ago`;
  //   } else {
  //     const diffInDays = Math.floor(diffInHours / 24);
  //     return `${diffInDays}d ago`;
  //   }
  // };

//   const formatDateTime1 = (isoString) => {
//   if (!isoString) return '-';

//   // Handle backend microseconds safely
//   const safeIso = isoString.includes('.')
//     ? isoString.substring(0, isoString.indexOf('.') + 4)
//     : isoString;

//   const date = new Date(safeIso);
//   const now = new Date();

//   if (isNaN(date.getTime())) return '-';

//   const diffInMs = now - date;
//   const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
//   const diffInHours = Math.floor(diffInMinutes / 60);
//   const diffInDays = Math.floor(diffInHours / 24);

//   // 1️⃣ Within 1 minute
//   if (diffInMinutes <= 1) {
//     return 'Just now';
//   }

//   // 2️⃣ Less than 24 hours → show date & time
//   if (diffInHours < 24) {
//     return (
//       date.toLocaleDateString('en-GB') +
//       ' ' +
//       date.toLocaleTimeString('en-US', {
//         hour: '2-digit',
//         minute: '2-digit'
//       })
//     );
//   }

//   // 3️⃣ 24 hours or more → days ago
//   return diffInDays === 1
//     ? '1 day ago'
//     : `${diffInDays} days ago`;
// };

const formatDateTime = (isoString) => {
  if (!isoString) return '-';

  // Remove microseconds if present
  const safeIso = isoString.includes('.')
    ? isoString.substring(0, isoString.indexOf('.'))
    : isoString;

  const date = new Date(safeIso);

  if (isNaN(date.getTime())) return '-';

  const pad = (num) => String(num).padStart(2, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();

  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const formatCustomFieldDisplayValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => (item === null || item === undefined ? '' : String(item).trim()))
      .filter((item) => item.length > 0);
    return parts.length > 0 ? parts.join(', ') : '-';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  const text = String(value).trim();
  return text.length > 0 ? text : '-';
};

  const fetchTicketsForExport = async () => {
    const params = { fetchAll: true, asRequester: true };
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.category && filters.category !== 'all') {
      const selectedCategory = categories.find(cat =>
        (cat.categoryName || '').toLowerCase() === filters.category.toLowerCase()
      );
      if (selectedCategory && selectedCategory.categoryId) params.categoryId = selectedCategory.categoryId;
    }
    if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
      params.search = debouncedSearchTerm.trim();
    }
    const response = await ticketService.getMyTickets(params);
    const responseData = response?.data;
    if (!responseData?.success || !responseData?.data) return [];
    const paginatedData = responseData.data;
    const content = paginatedData.content || [];
    const availablePriorities = prioritiesRef.current;
    const transformed = content.map((ticket) => {
      let priorityName = 'Medium';
      const priorityId = ticket.priorityId;
      if (priorityId && availablePriorities.length > 0) {
        const p = availablePriorities.find(pr => pr.priorityId === priorityId || String(pr.priorityId) === String(priorityId));
        if (p) priorityName = p.priorityName || p.priorityCode || 'Medium';
      } else if (ticket.priority) priorityName = ticket.priority;
      const assignedName = ticket.assigneeFullName || (ticket.assigneeId ? 'Assigned' : 'Unassigned');
      return {
        ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
        subject: ticket.subject || 'No Subject',
        category: ticket.category || 'Uncategorized',
        status: (ticket.status || 'open').toLowerCase(),
        assignedTo: assignedName,
        lastActivity: ticket.updatedAt || ticket.createdAt
      };
    });
    if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      return transformed.filter((t) =>
        (t.ticketNumber || '').toLowerCase().includes(searchLower) ||
        (t.subject || '').toLowerCase().includes(searchLower)
      );
    }
    return transformed;
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchTicketsForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const headers = ['S.NO', 'Ticket Number', 'Subject', 'Category', 'Status', 'Assigned To', 'Last Activity'];
      const excelRows = rows.map((r, i) => [
        i + 1,
        r.ticketNumber,
        r.subject,
        r.category,
        r.status,
        r.assignedTo,
        r.lastActivity || '',
      ]);
      downloadStyledXlsx({
        fileName: `my-tickets-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'My Tickets',
        headers,
        rows: excelRows,
        numberColumns: [0],
        textColumns: [1, 2, 3, 4, 5],
        dateColumns: [6],
        centerColumns: [0, 4, 6],
      });
      toast.success('Tickets exported as Excel');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchTicketsForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('My Tickets', 14, 15);
      doc.setFontSize(10);
      const body = rows.map((r) => [
        r.ticketNumber,
        (r.subject || '').substring(0, 40),
        (r.category || '').substring(0, 15),
        r.status,
        (r.assignedTo || '').substring(0, 18),
        r.lastActivity ? formatDateTime(r.lastActivity) : '-'
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['Ticket Number', 'Subject', 'Category', 'Status', 'Assigned To', 'Last Activity']],
        body,
        styles: { fontSize: 8 }
      });
      doc.save(`my-tickets-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Tickets exported as PDF');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Server-side filtering - tickets are already filtered by API
  // Just return the tickets from ticketsData
  const getTickets = () => {
    return ticketsData?.userTickets || [];
  };

  const fetchReplies = async (ticketId) => {
    try {
      setLoadingReplies(true);
      const response = await ticketService.getReplies(ticketId);
      
      // Extract replies from ResponseBean structure
      const responseData = response?.data;
      let repliesArray = [];
      
      if (responseData?.data) {
        // ResponseBean format: extract nested data
        repliesArray = Array.isArray(responseData.data) ? responseData.data : [];
      } else if (Array.isArray(responseData)) {
        // Direct array format
        repliesArray = responseData;
      }
      
      // Transform backend replies to frontend format
      const transformedReplies = repliesArray.map(reply => ({
        id: reply.replyId || reply.id,
        replyId: reply.replyId || reply.id,
        content: reply.content || reply.message || '',
        message: reply.content || reply.message || '',
        createdBy: reply.userName ? { name: reply.userName } : (reply.createdBy || { name: 'System' }),
        author: reply.userName || (reply.author?.name || reply.author || 'System'),
        createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
        timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
        isInternal: reply.isInternal || false,
        type: (reply.replyType === 'internal_note' || reply.replyType === 'unassignment') ? 'internal' : (reply.replyType || 'comment')
      }));
      
      // Filter out internal notes for portal users (they shouldn't see internal notes)
      const publicReplies = transformedReplies.filter(reply => !reply.isInternal);
      // Sort by date (newest first) - latest reply appears first
      const sortedReplies = publicReplies.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.timestamp || 0);
        const dateB = new Date(b.createdAt || b.timestamp || 0);
        return dateB - dateA; // Descending order (newest first)
      });
      setReplies(sortedReplies);
    } catch (error) {
      console.error('Error fetching replies:', error);
      toast.error('Failed to load replies');
      setReplies([]);
    } finally {
      setLoadingReplies(false);
    }
  };

  // Fetch attachments for a ticket
  const fetchAttachments = async (ticketId) => {
    try {
      setLoadingAttachments(true);
      const response = await ticketService.getAttachments(ticketId);
      
      // Extract attachments from ResponseBean structure
      const responseData = response?.data;
      let attachmentsArray = [];
      
      if (responseData?.data) {
        // ResponseBean format: extract nested data
        attachmentsArray = Array.isArray(responseData.data) ? responseData.data : [];
      } else if (Array.isArray(responseData)) {
        // Direct array format
        attachmentsArray = responseData;
      }
      
      setAttachments(attachmentsArray);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      toast.error('Failed to load attachments');
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };

  // Fetch replies and attachments when a ticket is selected
  useEffect(() => {
    if (selectedTicket?.id) {
      fetchReplies(selectedTicket.id);
      fetchAttachments(selectedTicket.id);
    } else {
      setReplies([]);
      setAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket?.id]);

  // Fetch feedback when a closed ticket is selected
  useEffect(() => {
    const fetchFeedback = async () => {
      // Only fetch if ticket is closed
      if (selectedTicket?.status?.toLowerCase() === 'closed' && selectedTicket?.id) {
        setLoadingFeedback(true);
        setTicketFeedback(null);
        try {
          // Extract numeric ticketId - backend expects numeric ID
          const ticketId = selectedTicket.ticketId || (typeof selectedTicket.id === 'number' ? selectedTicket.id : null);
          
          if (ticketId) {
            const response = await ticketService.getFeedback(ticketId);
            
            // Handle response structure: response.data.success and response.data.data
            if (response?.data?.success && response?.data?.data) {
              setTicketFeedback(response.data.data);
            } else if (response?.data?.data === null) {
              // No feedback found (backend returns null)
              setTicketFeedback(null);
            }
          }
        } catch (error) {
          // If no feedback found, backend might return error - that's okay
          //console.log('No feedback found for ticket:', error);
          setTicketFeedback(null);
        } finally {
          setLoadingFeedback(false);
        }
      } else {
        // Clear feedback if ticket is not closed
        setTicketFeedback(null);
      }
    };

    fetchFeedback();
  }, [selectedTicket?.id, selectedTicket?.status]);

  useEffect(() => {
    setShowFeedbackForm(false);
    setFeedbackStarsCount(0);
    setFeedbackHoveredStar(0);
    setFeedbackComment('');
  }, [selectedTicket?.id]);

  const handleSubmitFeedback = async () => {
    if (feedbackStarsCount === 0) {
      toast.error('Please provide a rating by selecting stars');
      return;
    }

    const ticketId =
      selectedTicket?.ticketId ||
      (typeof selectedTicket?.id === 'number' ? selectedTicket.id : null) ||
      selectedTicket?.id;

    if (!ticketId) {
      toast.error('Invalid ticket information');
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const response = await ticketService.submitFeedback({
        ticketId,
        starsCount: feedbackStarsCount,
        comment: feedbackComment.trim() || null
      });

      if (response?.data?.success) {
        toast.success('Thank you for your feedback!');
        setShowFeedbackForm(false);
        setFeedbackStarsCount(0);
        setFeedbackHoveredStar(0);
        setFeedbackComment('');

        const feedbackResponse = await ticketService.getFeedback(ticketId);
        if (feedbackResponse?.data?.success && feedbackResponse?.data?.data) {
          setTicketFeedback(feedbackResponse.data.data);
        }

        loadTicketsData(currentPage, pageSize);
      } else {
        throw new Error(response?.data?.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to submit feedback');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleAddReply = async () => {
    if (!selectedTicket?.id) {
      toast.error('Please select a ticket');
      return;
    }

    const trimmed = newReply.trim();
    const namesLine =
      replyFiles.length > 0
        ? `Attached file(s): ${replyFiles.map((f) => f.name).join(', ')}`
        : '';
    let content;
    if (trimmed.length >= 5) {
      content = trimmed;
    } else if (replyFiles.length > 0) {
      content = trimmed ? `${trimmed}\n\n${namesLine}` : namesLine;
    } else {
      toast.error('Please enter at least 5 characters or attach a file.');
      return;
    }
    if (content.trim().length < 5) {
      toast.error('Reply is too short. Add more text or attach a file.');
      return;
    }

    if (isAddingReply) {
      return;
    }

    setIsAddingReply(true);
    try {
      const response = await ticketService.addReply(selectedTicket.id, {
        content,
        replyChannel: 'web' // Track that reply came from web portal
      });

      const reply = extractSavedReplyFromResponse(response);
      if (!reply) {
        toast.error(extractErrorMessage(response?.data, 'Failed to add reply'));
        return;
      }

      const replyId = reply.replyId || reply.id;

      if (replyFiles.length > 0 && replyId) {
          const rawTicketId = selectedTicket.ticketId ?? selectedTicket.id;
          const parsedTicketId = parseInt(String(rawTicketId), 10);
          if (Number.isNaN(parsedTicketId)) {
            console.error('Invalid ticket ID for reply attachment upload:', rawTicketId);
            toast.error('Ticket ID is invalid. Attachments were not uploaded.');
          } else {
            try {
              for (const file of replyFiles) {
                await fileService.uploadFile(file, {
                  reg_id: parsedTicketId,
                  fileTypeId: 1,
                  isActive: true,
                  screenName: 'TICKET'
                });
              }
              toast.success(`${replyFiles.length} attachment(s) uploaded`);
              await fetchAttachments(selectedTicket.id);
            } catch (uploadError) {
              console.error('Error uploading reply attachments:', uploadError);
              toast.error('Reply added but some attachments failed to upload');
            }
          }
        }

        // Transform reply to frontend format
        const transformedReply = {
          id: reply.replyId || reply.id,
          replyId: reply.replyId || reply.id,
          content: reply.content || reply.message || '',
          message: reply.content || reply.message || '',
          createdBy: reply.userName ? { name: reply.userName } : (reply.createdBy || { name: 'System' }),
          author: reply.userName || (reply.author?.name || reply.author || 'System'),
          createdAt: reply.createdAt || reply.timestamp || new Date().toISOString(),
          timestamp: reply.createdAt || reply.timestamp || new Date().toISOString(),
          isInternal: reply.isInternal || false,
          type: (reply.replyType === 'internal_note' || reply.replyType === 'unassignment') ? 'internal' : (reply.replyType || 'comment')
        };

        // Add to replies list (only if not internal)
        if (!transformedReply.isInternal) {
          setReplies(prev => {
            const prevArray = Array.isArray(prev) ? prev : [];
            return [transformedReply, ...prevArray];
          });
        }

        setNewReply('');
        setReplyFiles([]);
        if (replyFileInputRef.current) {
          replyFileInputRef.current.value = '';
        }
        setShowReplyForm(false);
        toast.success('Reply added successfully');
    } catch (error) {
      console.error('Add reply error:', error);
      toast.error(extractErrorMessage(error, 'Failed to add reply'));
    } finally {
      setIsAddingReply(false);
    }
  };

  const handleReopenTicket = async () => {
    if (!selectedTicket?.id) {
      toast.error('Please select a ticket');
      return;
    }

    try {
      const reopenStatusId = 9; // Reopen status ID
      await ticketService.updateTicketStatus(selectedTicket.id, reopenStatusId);
      
      // Update the selected ticket status
      setSelectedTicket(prev => ({
        ...prev,
        status: 'reopen'
      }));
      
      // Reload tickets to reflect the change
      await loadTicketsData(currentPage, pageSize);
      
      toast.success('Ticket reopened successfully');
    } catch (error) {
      console.error('Error reopening ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to reopen ticket');
    }
  };

  const handleTicketAction = (actionId, ticketId) => {
    switch (actionId) {
      case 'action-001': // Add Comment
        alert('Add Comment functionality would be implemented here');
        break;
      case 'action-002': // View History
        alert('View History functionality would be implemented here');
        break;
      case 'action-003': // Close Ticket
        alert('Close Ticket functionality would be implemented here');
        break;
      case 'action-004': // Reopen Ticket
        alert('Reopen Ticket functionality would be implemented here');
        break;
      case 'action-005': // Escalate Ticket
        alert('Escalate Ticket functionality would be implemented here');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tickets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">{error}</p>
        <Button onClick={loadTicketsData} className="mt-4">Retry</Button>
      </div>
    );
  }

  if (!ticketsData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No tickets data available</p>
      </div>
    );
  }

  const tickets = getTickets();
  // Use allTicketsStats if available (from all tickets), otherwise fall back to ticketsData stats
  const stats = allTicketsStats || ticketsData?.ticketStats || {
    totalTickets: 0,
    openTickets: 0,
    inProgressTickets: 0,
    resolvedTickets: 0,
    closedTickets: 0,
    highPriorityTickets: 0,
    overdueTickets: 0,
    avgResolutionTime: '0h 0m'
  };

  return (
    <>
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideInScale {
          from {
            opacity: 0;
            transform: translateX(50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
      `}</style>
      
      <div className="w-full space-y-6 p-6" style={{ backgroundColor: '#F5F7FA' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            My Tickets
          </h1>
          <p className="text-gray-600">
            View and manage your support tickets
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Filter size={18} />
            <span>Filters</span>
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || exporting}
              className="flex items-center space-x-2 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Download size={18} />
              <span>{exporting ? 'Exporting…' : 'Export'}</span>
              <ChevronDown size={18} />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-[140px]">
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 flex items-center gap-2"
                  onClick={handleExportExcel}
                >
                  <FileText size={16} />
                  Export Excel
                </button>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 flex items-center gap-2"
                  onClick={handleExportPdf}
                >
                  <FileText size={16} />
                  Export PDF
                </button>
              </div>
            )}
          </div>
          <Button 
            type="button"
            onClick={handleCreateTicket}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={loading}
          >
            <Plus size={18} />
            <span>Create Ticket</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tickets</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTickets || 0}</p>
              </div>
              <Ticket className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Open Tickets</p>
                <p className="text-2xl font-bold text-gray-900">{stats.openTickets || 0}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">{stats.inProgressTickets || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Resolved</p>
                <p className="text-2xl font-bold text-gray-900">{stats.resolvedTickets || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-2">
                <Search className="h-4 w-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </div>
              <select 
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                {statuses.map(status => {
                  const statusCode = status.statusCode || status.statusName?.toLowerCase() || '';
                  return (
                    <option key={status.statusId || statusCode} value={statusCode}>
                      {status.statusName || status.statusCode}
                    </option>
                  );
                })}
              </select>
              {/* <select 
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Priority</option>
                {(ticketsData?.ticketFilters?.priorities || []).map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select> */}
              <select 
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.filter(cat => cat.isActive !== false && cat.categoryId !== null).map(category => (
                  <option key={category.categoryId} value={(category.categoryName || '').toLowerCase()}>
                    {category.categoryName || 'Uncategorized'}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6">
        {/* Tickets List */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Ticket className="h-5 w-5" />
                  <span>Your Tickets ({tickets.length} on this page)</span>
                </div>
                {paginationData && paginationData.totalElements > 0 && (
                  <span className="text-sm font-normal text-gray-500">
                    {paginationData.totalElements} total
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tickets.length === 0 ? (
                <div className="text-center py-12">
                  <Ticket className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No tickets found</p>
                  <p className="text-sm text-gray-500 mt-2">Try adjusting your filters or create a new ticket</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b-2 border-gray-200 bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Ticket Number
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Priority
                        </th> */}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Assigned To
                        </th>
                        {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          SLA Status
                        </th> */}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Last Activity
                        </th>
                        {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th> */}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tickets.map((ticket) => (
                        <tr 
                          key={ticket.id}
                          className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                            selectedTicket?.id === ticket.id ? 'bg-blue-50 ring-2 ring-blue-500' : ''
                          }`}
                          onClick={() => setSelectedTicket(ticket)}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(ticket.status)}
                              <span className="text-sm font-medium text-gray-900">
                                {ticket.ticketNumber || ticket.id}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={ticket.subject || 'No Subject'}>
                              {ticket.subject || 'No Subject'}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {ticket.category || 'Uncategorized'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge className={getStatusColor(ticket.status)}>
                              {ticket.status || 'open'}
                            </Badge>
                          </td>
                          {/* <td className="px-4 py-3 whitespace-nowrap">
                            <Badge className={getPriorityColor(ticket.priority || 'medium')}>
                              {ticket.priorityName || ticket.priority || 'medium'}
                            </Badge>
                          </td> */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            {ticket.assignedAgent ? (
                              <div className="flex items-center space-x-1 text-sm text-gray-600">
                                <User className="h-3 w-3" />
                                <span>{ticket.assignedAgent.name || 'Unassigned'}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">Unassigned</span>
                            )}
                          </td>
                          {/* <td className="px-4 py-3 whitespace-nowrap">
                            {ticket.slaStatus ? (
                              <span className={`text-xs font-medium ${getSlaStatusColor(ticket.slaStatus)}`}>
                                {ticket.slaStatus}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td> */}
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {formatDateTime(ticket.updatedAt)}
                            </span>
                          </td>
                          {/* <td className="px-4 py-3 whitespace-nowrap">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTicket(ticket);
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              View
                            </button>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Pagination Controls */}
              {paginationData && paginationData.totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-700">
                    <span>
                      Showing {paginationData.pageNumber * paginationData.pageSize + 1} to{' '}
                      {Math.min((paginationData.pageNumber + 1) * paginationData.pageSize, paginationData.totalElements)} of{' '}
                      {paginationData.totalElements} tickets
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={paginationData.first || currentPage === 0}
                      className="flex items-center space-x-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Previous</span>
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                        let pageNum;
                        if (paginationData.totalPages <= 5) {
                          pageNum = i;
                        } else if (currentPage < 3) {
                          pageNum = i;
                        } else if (currentPage > paginationData.totalPages - 4) {
                          pageNum = paginationData.totalPages - 5 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className={`min-w-[40px] ${
                              currentPage === pageNum 
                                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            {pageNum + 1}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(paginationData.totalPages - 1, prev + 1))}
                      disabled={paginationData.last || currentPage >= paginationData.totalPages - 1}
                      className="flex items-center space-x-1"
                    >
                      <span>Next</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Ticket Details Modal — portaled above layout (sidebar/header z-40/50) */}
      {selectedTicket &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[112] flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden sm:h-screen"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          >
            <div
              className="pointer-events-none absolute inset-0 bg-black/50 backdrop-blur-sm"
              aria-hidden
            />
            <div
              className="relative z-[113] flex min-h-0 flex-1 cursor-default items-center justify-center overflow-y-auto overflow-x-hidden p-0 sm:p-4"
              onClick={() => setSelectedTicket(null)}
              role="presentation"
            >
          <div 
            className="relative z-[125] m-0 flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl transform transition-all sm:h-auto sm:max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
              <CardTitle className="flex items-center space-x-2 text-xl">
                <FileText className="h-6 w-6 text-blue-600" />
                <span>Ticket Details</span>
              </CardTitle>
              <button
                onClick={() => setSelectedTicket(null)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="overflow-y-auto overflow-x-hidden flex-1 p-4 sm:p-6">
              <div className="space-y-5">
                <div className="space-y-3">
                  <div className="flex min-w-0 flex-wrap items-start gap-2">
                    <span className="text-sm font-medium text-gray-600 shrink-0">Subject:</span>
                    <span className="text-sm font-semibold text-gray-900 flex-1 min-w-0 break-words [overflow-wrap:anywhere] whitespace-pre-wrap">
                      {selectedTicket.subject || 'No Subject'}
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-start gap-2">
                    <span className="text-sm font-medium text-gray-600 shrink-0">Description:</span>
                    <p className="text-sm text-gray-600 flex-1 min-w-0 break-words [overflow-wrap:anywhere] whitespace-pre-wrap">
                      {selectedTicket.description || 'No description available'}
                    </p>
                  </div>
                </div>

                {selectedTicket.customFieldValues &&
                  typeof selectedTicket.customFieldValues === 'object' &&
                  Object.keys(selectedTicket.customFieldValues).length > 0 && (
                    <div className="space-y-2 border-t border-gray-200 pt-4">
                      <div className="space-y-2">
                        {Object.entries(selectedTicket.customFieldValues).map(([fieldCode, fieldValue]) => (
                          <div key={fieldCode} className="flex min-w-0 flex-wrap items-start gap-2">
                            <span className="text-sm font-medium text-gray-600 shrink-0">{fieldCode}:</span>
                            <span className="text-sm text-gray-900 flex-1 min-w-0 break-words [overflow-wrap:anywhere] whitespace-pre-wrap">
                              {formatCustomFieldDisplayValue(fieldValue)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                <div className="space-y-3 border-t border-gray-200 pt-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600 shrink-0">Ticket Details:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {selectedTicket.ticketNumber ||
                        (selectedTicket.ticketId != null ? String(selectedTicket.ticketId) : String(selectedTicket.id ?? ''))}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600 shrink-0">Status:</span>
                    <Badge className={getStatusColor(selectedTicket.status)}>
                      {selectedTicket.status || 'open'}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600 shrink-0">Category:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedTicket.category || 'Uncategorized'}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600 shrink-0">Created:</span>
                    <span className="text-sm text-gray-900">{formatDateTime(selectedTicket.createdAt)}</span>
                  </div>
                  {selectedTicket.assignedAgent && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-gray-600 shrink-0">Assigned To:</span>
                      <span className="text-sm font-medium text-gray-900">{selectedTicket.assignedAgent.name || 'Unassigned'}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600 shrink-0">Country:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedTicket.countryName || 'N/A'}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600 shrink-0">Region:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedTicket.regionName || 'N/A'}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600 shrink-0">County:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedTicket.countyName || 'N/A'}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600 shrink-0">Sub County:</span>
                    <span className="text-sm font-medium text-gray-900">{selectedTicket.subCountyName || 'N/A'}</span>
                  </div>
                </div>

                {/* Customer Feedback Section - Only show for closed tickets */}
                {selectedTicket?.status?.toLowerCase() === 'closed' && (
                  <div className="border-t border-gray-200 pt-5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        <span>Customer Feedback</span>
                      </h4>
                      {!loadingFeedback && !ticketFeedback && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowFeedbackForm(!showFeedbackForm)}
                          className="flex items-center space-x-1 bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500"
                        >
                          <Star className="h-3 w-3" />
                          <span>{showFeedbackForm ? 'Cancel' : 'Give Feedback'}</span>
                        </Button>
                      )}
                    </div>
                    {loadingFeedback ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600 mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600">Loading feedback...</p>
                      </div>
                    ) : ticketFeedback ? (
                      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-5 w-5 ${
                                  star <= (ticketFeedback.starsCount || 0)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {ticketFeedback.starsCount || 0} out of 5
                          </span>
                        </div>
                        {ticketFeedback.comment && (
                          <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                            {ticketFeedback.comment}
                          </p>
                        )}
                        {ticketFeedback.createdOn && (
                          <p className="text-xs text-gray-500 mt-2">
                            Submitted on {formatDateTime(ticketFeedback.createdOn)}
                          </p>
                        )}
                      </div>
                    ) : showFeedbackForm ? (
                      <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Rate your experience
                        </label>
                        <div className="flex items-center gap-2 mb-4">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setFeedbackStarsCount(star)}
                              onMouseEnter={() => setFeedbackHoveredStar(star)}
                              onMouseLeave={() => setFeedbackHoveredStar(0)}
                              className="focus:outline-none transition-transform hover:scale-110"
                              aria-label={`Rate ${star} star${star !== 1 ? 's' : ''}`}
                            >
                              <Star
                                className={`h-8 w-8 ${
                                  star <= (feedbackHoveredStar || feedbackStarsCount)
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                } transition-colors`}
                              />
                            </button>
                          ))}
                          {feedbackStarsCount > 0 && (
                            <span className="ml-2 text-sm text-gray-600">
                              {feedbackStarsCount} {feedbackStarsCount === 1 ? 'star' : 'stars'}
                            </span>
                          )}
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Comments (optional)
                          </label>
                          <textarea
                            value={feedbackComment}
                            onChange={(e) => setFeedbackComment(e.target.value)}
                            placeholder="Share your thoughts about the support you received..."
                            rows={4}
                            maxLength={500}
                            disabled={isSubmittingFeedback}
                            className="w-full p-3 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {feedbackComment.length}/500 characters
                          </p>
                        </div>
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowFeedbackForm(false);
                              setFeedbackStarsCount(0);
                              setFeedbackHoveredStar(0);
                              setFeedbackComment('');
                            }}
                            disabled={isSubmittingFeedback}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSubmitFeedback}
                            disabled={isSubmittingFeedback || feedbackStarsCount === 0}
                            className="flex items-center space-x-1 bg-yellow-500 hover:bg-yellow-600 text-white"
                          >
                            {isSubmittingFeedback ? (
                              <>
                                <Loader2 className="h-3 w-3 animate-spin" />
                                <span>Submitting...</span>
                              </>
                            ) : (
                              <>
                                <Send className="h-3 w-3" />
                                <span>Submit Feedback</span>
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600">No feedback submitted yet.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Replies Section */}
                <div className="border-t border-gray-200 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 flex items-center space-x-2">
                      <MessageSquare className="h-4 w-4" />
                      <span>Replies & Communication</span>
                    </h4>
                    <div className="flex items-center space-x-2">
                      {/* {selectedTicket?.status?.toLowerCase() === 'closed' &&
                        !loadingFeedback &&
                        !ticketFeedback && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowFeedbackForm(true);
                            }}
                            className="flex items-center space-x-1 bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500"
                          >
                            <Star className="h-3 w-3" />
                            <span>Feedback</span>
                          </Button>
                        )} */}
                      {/* Reopen Button - Only show when status is closed */}
                      {selectedTicket?.status?.toLowerCase() === 'closed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleReopenTicket}
                          className="flex items-center space-x-1 bg-red-700 hover:bg-red-800 text-white border-red-700"
                        >
                          <RotateCcw className="h-3 w-3" />
                          <span>Reopen</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowReplyForm(!showReplyForm)}
                        className="flex items-center space-x-1"
                      >
                        <MessageSquare className="h-3 w-3" />
                        <span>Add Reply</span>
                      </Button>
                    </div>
                  </div>

                  {/* Add Reply Form */}
                  {showReplyForm && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                      <div className="mb-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Your Reply
                        </label>
                        <textarea
                          value={newReply}
                          onChange={(e) => setNewReply(e.target.value)}
                          placeholder="Enter your reply..."
                          disabled={isAddingReply}
                          className="w-full p-3 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          rows={4}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Attachments (optional)
                        </label>
                        <input
                          ref={replyFileInputRef}
                          type="file"
                          multiple
                          onChange={(e) => setReplyFiles(Array.from(e.target.files || []))}
                          disabled={isAddingReply}
                          className="w-full p-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 file:mr-3 file:rounded file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        {replyFiles.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {replyFiles.map((file, idx) => (
                              <li
                                key={`${file.name}-${idx}`}
                                className="flex items-center justify-between gap-2 text-sm text-gray-600 bg-white border border-gray-200 rounded px-2 py-1"
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  <Paperclip className="h-4 w-4 shrink-0 text-gray-500" />
                                  <span className="truncate">{file.name}</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setReplyFiles((prev) => prev.filter((_, i) => i !== idx));
                                    if (replyFileInputRef.current) {
                                      replyFileInputRef.current.value = '';
                                    }
                                  }}
                                  disabled={isAddingReply}
                                  className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50"
                                  aria-label={`Remove ${file.name}`}
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          Files upload after you send the reply (max 5 MB each). They appear under Attachments.
                        </p>
                      </div>
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowReplyForm(false);
                            setNewReply('');
                            setReplyFiles([]);
                            if (replyFileInputRef.current) {
                              replyFileInputRef.current.value = '';
                            }
                          }}
                          disabled={isAddingReply}
                          className="flex items-center space-x-1"
                        >
                          <X className="h-3 w-3" />
                          <span>Cancel</span>
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          onClick={handleAddReply}
                          disabled={
                            isAddingReply ||
                            !(replyFiles.length > 0 || newReply.trim().length >= 5)
                          }
                          className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white min-w-[7.5rem] justify-center"
                        >
                          {isAddingReply ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <Send className="h-3 w-3 shrink-0" />
                              <span>Send Reply</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Replies List */}
                  {loadingReplies ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">Loading replies...</p>
                    </div>
                  ) : replies.length > 0 ? (
                    <div
                      className="space-y-3 max-h-[min(28rem,50vh)] overflow-y-auto overflow-x-hidden overscroll-y-contain rounded-lg border border-gray-100 bg-gray-50/50 p-2 shadow-inner
                      [scrollbar-width:thin]
                      [&::-webkit-scrollbar]:w-2
                      [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100
                      [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300
                      hover:[&::-webkit-scrollbar-thumb]:bg-gray-400"
                    >
                      {replies.map((reply) => (
                        <div key={reply.id} className="min-w-0 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex min-w-0 justify-between items-start gap-2 mb-2">
                            <div className="flex min-w-0 items-center space-x-2">
                              <User className="h-4 w-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-900">
                                {reply.createdBy?.name || reply.author?.name || reply.author || 'Unknown'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {displayTimezone
                                ? new Date(reply.createdAt || reply.timestamp).toLocaleString('en-GB', { timeZone: displayTimezone, hour12: false })
                                : '…'}
                            </span>
                          </div>
                          <p className="max-w-full break-words text-sm text-gray-700 [overflow-wrap:anywhere] whitespace-pre-wrap">
                            {reply.content || reply.message}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
                      <MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">No replies yet</p>
                      <p className="text-xs text-gray-500 mt-1">Be the first to reply to this ticket</p>
                    </div>
                  )}
                </div>

                {/* Attachments */}
                <div className="border-t border-gray-200 pt-5">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Paperclip className="h-4 w-4 mr-2" />
                    Attachments
                  </h4>
                  {loadingAttachments ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">Loading attachments...</p>
                    </div>
                  ) : attachments.length > 0 ? (
                    <div className="space-y-2">
                      {attachments.map((attachment) => (
                        <div
                          key={attachment.id || attachment.attachmentId}
                          className="flex items-start justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex gap-2 flex-1 min-w-0">
                            <Paperclip className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" aria-hidden />
                            <div className="min-w-0 flex-1 space-y-0.5">
                              <p
                                className="text-sm font-medium text-gray-900 truncate"
                                title={attachment.name || attachment.fileName}
                              >
                                {attachment.name || attachment.fileName || 'Unnamed file'}
                              </p>
                              {(attachment.uploaderName || attachment.uploaderRole) && (
                                <p className="text-xs text-gray-600 break-words">
                                  Uploaded by:{' '}
                                  <span className="font-medium text-gray-800">
                                    {attachment.uploaderName || 'Unknown'}
                                    {attachment.uploaderRole ? ` (${attachment.uploaderRole})` : ''}
                                  </span>
                                </p>
                              )}
                              <p className="text-xs text-gray-500">
                                {formatTicketFileUploadedAt(attachment.createdOn || attachment.createdAt)}
                              </p>
                              {attachment.size ? (
                                <p className="text-xs text-gray-500">
                                  ({(attachment.size / 1024).toFixed(2)} KB)
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-0.5 pt-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              className="h-8 w-8 p-0 hover:bg-emerald-50"
                              onClick={() => {
                                const url = resolveAttachmentOpenUrl(attachment);
                                if (url) {
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                } else {
                                  toast.error('File path not available');
                                }
                              }}
                              title={`View ${attachment.name || attachment.fileName || 'attachment'}`}
                              aria-label={`View ${attachment.name || attachment.fileName || 'attachment'}`}
                            >
                              <Eye className="h-4 w-4 text-gray-600 hover:text-emerald-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              className="h-8 w-8 p-0 flex-shrink-0 hover:bg-blue-100"
                              onClick={() => {
                                const url = resolveAttachmentOpenUrl(attachment);
                                if (url) {
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                } else {
                                  toast.error('File path not available');
                                }
                              }}
                              title={`Download ${attachment.name || attachment.fileName || 'attachment'}`}
                              aria-label={`Download ${attachment.name || attachment.fileName || 'attachment'}`}
                            >
                              <Download className="h-4 w-4 text-gray-600 hover:text-blue-600" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                      <Paperclip className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">No attachments</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {(ticketsData?.ticketActions && ticketsData.ticketActions.length > 0) && (
                  <div className="border-t border-gray-200 pt-5">
                    <h4 className="font-semibold text-gray-900 mb-3">Actions</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {ticketsData.ticketActions.map((action) => (
                        <Button
                          key={action.id}
                          variant="outline"
                          size="sm"
                          onClick={() => handleTicketAction(action.id, selectedTicket.id)}
                          className="flex items-center justify-center space-x-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                          {action.icon === 'message-circle' && <MessageCircle className="h-3 w-3" />}
                          {action.icon === 'history' && <History className="h-3 w-3" />}
                          {action.icon === 'x-circle' && <XCircle className="h-3 w-3" />}
                          {action.icon === 'refresh-cw' && <RefreshCw className="h-3 w-3" />}
                          {action.icon === 'arrow-up' && <ArrowUp className="h-3 w-3" />}
                          <span className="text-xs">{action.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
            </div>
          </div>,
          document.body
        )}

      </div>
    </>
  );
};

export default MyTickets;